import { isPast } from "@gigpro/utils/datetime";
import { makeApi } from "@zodios/core";
import { makeClientWithHooks } from "api/helpers";
import { type AdminGigApplication, adminGigApplicationServerSchema } from "api/resources/gigApplications";
import { makePaginatedResult, paginationParams } from "api/resources/resource";
import { postProcessServerApplication } from "utils/GigUtil";
import { z } from "zod";
import { AdminBaseClient } from "./base";

class AdminGigApplicationClient extends AdminBaseClient<AdminGigApplication, true> {
  adminEndpoint = () => "/gig-applications";
}

export function postProcessServerAdminApplication(ogApp: z.infer<typeof adminGigApplicationServerSchema>) {
  const application = postProcessServerApplication(ogApp);
  let status: AdminGigApplication["status"];
  switch (true) {
    case application.is_cancelled || application.gig_status === "CANCELLED":
      status = "CANCELLED";
      break;
    case application.payment?.isFailed:
      status = "BUSINESS_PAYMENT_FAILED";
      break;
    case application.payment?.isProcessing:
      status = "BUSINESS_PAYMENT_PROCESSING";
      break;
    case application.payment?.isPaid:
      status = "PAID";
      break;
    case application.pro_payment?.isPaid:
      status = "PRO_PAID";
      break;
    case application.pro_payment?.isFailed:
      status = "PRO_PAYMENT_FAILED";
      break;
    case application.pro_payment?.isProcessing:
      status = "PRO_PAYMENT_PROCESSING";
      break;

    case application.is_confirmed: {
      const completed =
        application.working_hours.end_time ? isPast(application.working_hours.end_time, application.timezone) : false;
      status = completed ? "UNPAID" : "CONFIRMED";
      break;
    }
    case application.is_selected:
      status = "SELECTED";
      break;
    default:
      status = "CREATED";
      break;
  }
  return { ...application, status };
}
const adminGigApplicationResponseSchema = adminGigApplicationServerSchema.transform(postProcessServerAdminApplication);

export const adminApplicationsClient = new AdminGigApplicationClient();

const proPaymentSchema = z.object({ gig_application: z.number() });

export const adminGigApplicationListParams = [
  ...paginationParams,
  {
    name: "ordering",
    type: "Query",
    schema: z.string().optional(),
  },
  {
    name: "pro_id",
    type: "Query",
    schema: z.number().optional(),
  },
  {
    name: "unpaid_pros",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "uncharged_businesses",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "location_name",
    type: "Query",
    schema: z.string().optional(),
  },
  {
    name: "gig_id",
    type: "Query",
    schema: z.number().optional(),
  },
  {
    name: "is_confirmed",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "is_cancelled",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "is_selected",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "is_boosted",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "is_gig_cancelled",
    type: "Query",
    schema: z.boolean().optional(),
  },
  {
    name: "gig__start_time__gte",
    type: "Query",
    schema: z.coerce.date().optional(),
  },
  {
    name: "gig__start_time__lte",
    type: "Query",
    schema: z.coerce.date().optional(),
  },
] as const;

const adminApplicationsApi = makeApi([
  {
    method: "get",
    path: "/admin/gig-applications/",
    parameters: adminGigApplicationListParams,
    response: makePaginatedResult(adminGigApplicationResponseSchema),
    alias: "getApplications",
  },
  {
    method: "get",
    path: "/admin/gig-applications/:id/",
    response: adminGigApplicationResponseSchema,
    alias: "getApplication",
  },
  {
    method: "post",
    path: "/admin/pro-payments/",
    parameters: [{ name: "application_id", type: "Body", schema: proPaymentSchema }],
    response: proPaymentSchema,
    alias: "triggerProPayment",
  },
]);

export const { hooks: adminGigApplicationHooks } = makeClientWithHooks("adminApplicationsClient", adminApplicationsApi);
